import ContentBody from '@op/components/ContentBody';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_U } from '@op/utils/format';
import React, { useEffect, useMemo, useState } from 'react';
import { useDrilldownParams } from '@op/hooks/useDrilldownParams';
import {
  Button,
  Justify,
  Layout,
  Segment,
  TabPanel,
  Tabs,
} from 'tea-component';
import { connect, useHistory, useLocation } from 'umi';
import { dealwithConfig } from './utils';
import { NO_DATE } from '@op/constants';
import { ChannelType } from '../stats/Detail';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import { SortBy } from 'tea-component/lib/table/addons';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import { downloadAction } from '@op/components/DownloadCommon/download-page';
import CardCommon from '@op/components/CardCommon';
import CacheExpiredTips from '@op/components/CacheExpiredTips';
import SvgIcon from '@home/components/SvgIcon';
import { handleViewMore } from '../stats/utils';
import DetailKeyword from './component/DetailKeyword';
import DetailSubtitle from './component/DetailSubtitle';
import './index.scss';
import { GameStoreTable, SocialMediaTable, VideoTagTable } from './component/DetailTable';
import { ClassSpecial } from '@op/constants/common';
import { IDetailOptionType } from './types';
import ChannelsShareInfo from '@op/pages/community/streaming/widgets/ChannelsShare';

const tabs = [
  {
    key: 'social_total',
    text: 'Social Media',
    value: 'social',
  },
  {
    key: 'comments_total',
    text: 'Game Store',
    value: 'comments',
  },
];

const { Content } = Layout;

function useSortByInfo({ channelType }: { channelType: ChannelType }) {
  const [options, setOptions] = useState<{ text: string; value: string }[]>([]);
  const socialMediaSortByInfo = useState<SortBy<any>>({
    by: 'engagement',
    order: 'desc',
  });
  const gameStoreSortByInfo = useState<SortBy<any>>({
    by: 'comment_time',
    order: 'desc',
  });

  const [sortBy, setSortBy] = useMemo(() => {
    return channelType === 'social'
      ? socialMediaSortByInfo
      : gameStoreSortByInfo;
  }, [channelType, socialMediaSortByInfo, gameStoreSortByInfo]);

  const sortList = useMemo(() => {
    return [
      {
        key: 'sortBy',
        defaultValue: sortBy.by,
        defaultParams: {
          options,
          onChange: (key: string) => {
            setSortBy({ order: 'desc', by: key });
          },
        },
      },
    ];
  }, [channelType, options, sortBy]);

  return { sortList, sortBy, setSortBy, setOptions };
}

function Detail({
  userInfo,
  gameInfo,
  wholeGameInfo,
}: {
  userInfoLoading: boolean;
  userInfo: any;
  gameInfo: any;
  wholeGameInfo: any;
}) {
  const { formatMessage, isEn } = useLanguage();
  const history = useHistory();
  const location = useLocation();
  const [filter, setFilter] = useState<any>(null);
  const [detailFilter, setDetailFilter] = useState<any>(null);
  const [title, setTitle] = useState<string | undefined>('');
  const [titleCn, setTitleCn] = useState<string | undefined>('');
  const [feedsStatsLoading, setFeedsStatsLoading] = useState<boolean>(true);
  const [buriedPoint, setBuriedPoint] = useState<any>(null);
  const [config, setConfig] = useState<{ Component: any; defaultProps: any }>();
  const [selectRenderKeyword, setSelectRenderKeyword] = useState<{
    origin: string[];
    zh: string[];
    en: string[];
  }>({ origin: [], zh: [], en: [] });
  const [total, setTotal] = useState(0);
  const [selectKeyword, setSelectKeyword] = useState<Array<string>>([]);
  const [statsData, setStatsData] = useState<any>({});
  const [feedsCommentsLoading, setFeedsCommentsLoading] = useState(false);
  const [channelType, setChannelType] = useState<ChannelType>('social');
  const { sortList, sortBy, setSortBy, setOptions } = useSortByInfo({
    channelType,
  });
  const [detailPage, setDetailPage] = useState<IDetailOptionType>({
    showViewMore: true,
  });
  const { readParams } = useDrilldownParams();
  /** 参数读取失败（缓存失效）时展示兜底提示 */
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    async function loadParams() {
      // 方案 1：从服务端/本地存储读取（由 Hook 内部处理）
      let pageParams: any = await readParams();

      // 兼容：goPage 存储的数据结构为 { filter, path, extra, stats }，详情页完整数据存放在 filter 字段中
      if (pageParams && !pageParams.key && pageParams?.filter?.key) {
        pageParams = pageParams.filter;
      }

      // 方案 2：原有 sessionStorage 逻辑（兼容无 _s 标记的旧 URL）
      if (!pageParams) {
        pageParams = DBO_U.detailPage();
      }

      if (!pageParams) {
        setInvalid(true);
        return;
      }

      const {
        title,
        filter,
        buriedPoint,
        Component,
        defaultProps,
        detailFilter,
        detailPageOptions,
        titleCn
      } = dealwithConfig(pageParams);
      setTitle(title);
      setTitleCn(titleCn);
      setFilter(filter);
      setDetailFilter(detailFilter);
      setConfig({ Component, defaultProps });
      setBuriedPoint(buriedPoint);
      setDetailPage((prev) => ({ ...prev, ...detailPageOptions }));
    }
    loadParams();
  }, []);

  useEffect(() => {
    if (!detailFilter?.sort_item) return;
    if (detailFilter?.sort_item) {
      setSortBy({ order: 'desc', by: detailFilter.sort_item });
    }
  }, [detailFilter?.sort_item]);

  const [totalMap, setTotalMap] = useState<any>({});
  const filterParams = useMemo(() => {
    if (!filter || !detailFilter) {
      return {};
    }
    const { version_times, version } = filter || {};

    if (version?.length > 0) {
      return {
        ...filter,
        ...detailFilter,
        version_times: version_times.filter((item: any) =>
          version.includes(item.release_time),
        ),
      };
    }

    return {
      ...filter,
      ...detailFilter,
    };
  }, [filter, detailFilter]);

  const { Sentiment_Rating } = useGetSingePageSourceRule({
    keys: 'Sentiment_Rating',
  });

  const handleStatsData = (data: any) => {
    setStatsData(data);
    const selectGameStore =
      typeof data.social_total === 'undefined' || !data.social_total;
    setChannelType(selectGameStore ? 'comments' : 'social');
  };

  const addTotalString = (key: string) => {
    if (typeof statsData[key] !== 'undefined') {
      return ` (${statsData[key]})`;
    }
    return '';
  };

  // 返回按钮：优先跳 redirect_path，否则回上一页
  const handleBack = () => {
    const redirectPath = (location as any).query?.redirect_path;
    if (redirectPath) {
      history.push(decodeURIComponent(redirectPath));
    } else {
      history.goBack();
    }
  };

  const header = (
    <Content.Header
      showBackButton
      onBackButtonClick={handleBack}
      title={!isEn && titleCn ? titleCn : formatMessage({ id: title || NO_DATE.EMPTY })}
    ></Content.Header>
  );

  // 缓存失效：展示统一兜底提示（含「返回首页」按钮跳 /opinion）
  if (invalid) {
    return (
      <Content className="dbo-keywords-details">
        <ContentBody>
          <CacheExpiredTips scene="drilldown" />
        </ContentBody>
      </Content>
    );
  }

  return (
    <Content className="dbo-keywords-details">
      <ContentBody contentHeard={header}>
        <>
          {config?.Component && (
            <config.Component
              {...config.defaultProps}
              isDetail
              updateDetailFilter={(params: any) => {
                setDetailFilter(params);
              }}
            ></config.Component>
          )}
        </>
        <DetailSubtitle filter={filter} detailFilter={detailFilter} defaultProps={config?.defaultProps} />
        {detailPage.tableMode === 'streaming' ? (
          <ChannelsShareInfo
            metric={config?.defaultProps?.keyMetric?.value || 'hoursWatched'}
            params={{
              ...filterParams,
              channels: filterParams.channels ?? filterParams.channel,
            }}
            gameInfo={gameInfo}
            gameName={wholeGameInfo?.entity_name}
            showTitleTips={!wholeGameInfo?.is_expanded}
          />
        ) : (
          <>
            <DetailKeyword
              setSelectRenderKeyword={setSelectRenderKeyword}
              filter={filterParams}
              tableTotal={total}
              backCommentTotal={(value: number) => setTotal(value || 0)}
              selectKeyword={selectKeyword}
              setSelectKeyword={setSelectKeyword}
              handleStatsData={handleStatsData}
              feedsStatsLoading={feedsStatsLoading}
              setFeedsStatsLoading={setFeedsStatsLoading}
              commentLoading={feedsCommentsLoading}
            ></DetailKeyword>
            <FilterBoxCommon
              className="dbo-stats-selectbox"
              left={sortList as any}
              right={[
                {
                  key: 'download',
                  event: {
                    onClick: () => {
                      const map = {
                        social: 'Social Media Feeds',
                        comments: 'Game Store Feeds',
                        news: 'News Media Feed',
                      };

                      const name = map[channelType];

                      const downloadLimit = 150000; // 下载限制条数

                      const gameName = wholeGameInfo?.entity_name || '';

                      const fileName = `${gameName} ${name} - DataBrain Opinions`;

                      const downLoadParams = {
                        ...gameInfo,
                        ...filterParams,
                        statistics_field: 'keyword',
                        channel_type: channelType,
                        sort_item: sortBy.by,
                        sort_model: sortBy.order,
                        keyword: selectKeyword,
                        hideSentimentRating: Sentiment_Rating,
                        page: 1,
                        page_size: 1000,
                      };

                      downloadAction({
                        limit: downloadLimit,
                        fileName,
                        downLoadParams,
                        downloadFunc: 'getReplayDownload',
                        sheetName: name,
                        total: totalMap?.[channelType] || 0,
                      });
                    },
                  },
                },
              ]}
              onChange={() => { }}
            ></FilterBoxCommon>
            <CardCommon
              className="details-table"
              loading={feedsStatsLoading}
              loadingHeight={300}
            >
              {detailPage.tableMode === 'video' ? (
                <VideoTagTable
                  setSortBy={setSortBy}
                  sortBy={sortBy}
                  filter={filterParams}
                  selectKeyword={selectKeyword}
                  backLoading={setFeedsCommentsLoading}
                  backCommentTotal={(value) => {
                    setTotal(value || 0);
                  }}
                  gameInfo={gameInfo}
                  wholeGameInfo={wholeGameInfo}
                  setSortByOptions={setOptions}
                />
              ) : (
                <>
                  <Justify
                    className="dbo-stats-feeds-justify"
                    left={
                      <Segment
                        value={channelType.toString()}
                        onChange={(value: ChannelType) => {
                          setChannelType(value);
                        }}
                        options={tabs.map(({ text, value, key }) => ({
                          text: formatMessage(text) + addTotalString(key),
                          value,
                        }))}
                      />
                    }
                    right={
                      <>
                        {detailPage.showViewMore && (
                          <Button
                            type="link"
                            className="details-more"
                            onClick={() => {
                              handleViewMore(channelType, history);
                            }}
                          >
                            {formatMessage({ id: 'View More' })}

                            <span>
                              <SvgIcon
                                type="arrow-right"
                                className="details-more-icon"
                              />
                            </span>
                          </Button>
                        )}
                      </>
                    }
                  />
                  <Tabs
                    tabs={tabs.map((item) => ({ label: item.text, id: item.value }))}
                    activeId={channelType}
                    className={ClassSpecial.TAB_NONE}
                  >
                    <TabPanel id={'social'}>
                      <SocialMediaTable
                        setSortBy={setSortBy}
                        sortBy={sortBy}
                        renderKeyword={selectRenderKeyword}
                        channelType={'social'}
                        filter={filterParams}
                        selectKeyword={selectKeyword}
                        backLoading={setFeedsCommentsLoading}
                        backCommentTotal={(value) => {
                          setTotalMap({ ...totalMap, [channelType]: value });
                        }}
                        gameInfo={gameInfo}
                        wholeGameInfo={wholeGameInfo}
                        setSortByOptions={setOptions}
                        defaultLanguages={detailPage.defaultLanguages}
                      />
                    </TabPanel>
                    <TabPanel id="comments">
                      <GameStoreTable
                        setSortBy={setSortBy}
                        sortBy={sortBy}
                        renderKeyword={selectRenderKeyword}
                        channelType={'comments'}
                        filter={filterParams}
                        selectKeyword={selectKeyword}
                        backLoading={setFeedsCommentsLoading}
                        backCommentTotal={(value) => {
                          setTotalMap({ ...totalMap, [channelType]: value });
                        }}
                        gameInfo={gameInfo}
                        wholeGameInfo={wholeGameInfo}
                        setSortByOptions={setOptions}
                        defaultLanguages={detailPage.defaultLanguages}
                      />
                    </TabPanel>
                  </Tabs>
                </>
              )}
            </CardCommon>
          </>
        )}
      </ContentBody>
    </Content>
  );
}

export default connect((state: any) => {
  const { loading, home, opinionCommon } = state;

  return {
    userInfoLoading: loading.effects['home/getUserInfo'],
    userInfo: home.userInfo,
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
  };
})(Detail);
